import React, { useState } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Copy, Check, Terminal } from 'lucide-react';
import { triggerHaptic } from '../services/purrSynthesizer';

interface FormattedMessageProps {
  content: string;
  isAssistant?: boolean;
}

export const FormattedMessage: React.FC<FormattedMessageProps> = ({ content, isAssistant }) => {
  const [copiedIndex, setCopiedIndex] = useState<number | null>(null);

  const handleCopy = (code: string, index: number) => {
    navigator.clipboard.writeText(code);
    setCopiedIndex(index);
    triggerHaptic(20);
    setTimeout(() => setCopiedIndex(null), 2000);
  };

  return (
    <div className="prose prose-invert max-w-none text-slate-100 leading-relaxed break-words font-sans">
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        components={{
          // Paragraph formatting & Cat Mannerism Detection
          p({ children }) {
            const text = String(children);
            // Check if paragraph is a cat action like *Purrs at 42.8 Hz*
            if (isAssistant && text.startsWith('*') && text.endsWith('*') && !text.slice(1, -1).includes('*')) {
              return (
                <div className="my-2 px-3 py-1.5 rounded-xl bg-amber-500/10 border border-fissal-gold/35 text-amber-200 italic text-xs flex items-center gap-2 shadow-sm">
                  <span className="text-sm">🐾</span>
                  <span>{text.slice(1, -1)}</span>
                </div>
              );
            }
            return <p className="mb-2.5 last:mb-0 text-[13px] sm:text-sm leading-relaxed text-slate-100">{children}</p>;
          },

          // Bold text enhancement for easy scanning
          strong({ children }) {
            return <strong className="font-bold text-white tracking-wide">{children}</strong>;
          },

          // Headers
          h1({ children }) {
            return (
              <h1 className="font-cinzel text-base font-bold text-fissal-goldLight mt-3.5 mb-2 pb-1 border-b border-white/10 tracking-wide">
                {children}
              </h1>
            );
          },
          h2({ children }) {
            return (
              <h2 className="font-cinzel text-sm font-bold text-sky-300 mt-3 mb-1.5 tracking-wide">
                {children}
              </h2>
            );
          },
          h3({ children }) {
            return (
              <h3 className="text-xs font-bold text-emerald-300 mt-2.5 mb-1">
                {children}
              </h3>
            );
          },

          // Lists (Enhanced spacing and font size for comfortable reading on phones)
          ul({ children }) {
            return <ul className="list-disc list-inside space-y-1.5 my-2.5 text-[13px] sm:text-sm text-slate-200 pl-1.5 leading-relaxed">{children}</ul>;
          },
          ol({ children }) {
            return <ol className="list-decimal list-inside space-y-1.5 my-2.5 text-[13px] sm:text-sm text-slate-200 pl-1.5 leading-relaxed">{children}</ol>;
          },
          li({ children }) {
            return <li className="leading-relaxed my-0.5">{children}</li>;
          },

          // Blockquotes
          blockquote({ children }) {
            return (
              <blockquote className="my-2.5 pl-3 border-l-2 border-fissal-gold/60 bg-white/5 py-1.5 pr-2 rounded-r-lg italic text-xs text-slate-300">
                {children}
              </blockquote>
            );
          },

          // Code blocks & inline code
          code(props) {
            const { inline, className, children, ...rest } = props as any;
            const match = /language-(\w+)/.exec(className || '');
            const codeString = String(children).replace(/\n$/, '');

            if (!inline && (match || codeString.includes('\n'))) {
              const lang = match ? match[1] : 'sh';
              const codeIdx = Math.random();

              return (
                <div className="my-2.5 rounded-xl overflow-hidden border border-white/15 bg-[#05080e] shadow-lg">
                  {/* Code Bar */}
                  <div className="bg-black/60 px-3 py-1 flex items-center justify-between border-b border-white/10 text-[10px] font-mono text-slate-400">
                    <div className="flex items-center gap-1.5">
                      <Terminal className="w-3 h-3 text-fissal-gold" />
                      <span className="uppercase">{lang}</span>
                    </div>
                    <button
                      onClick={() => handleCopy(codeString, codeIdx)}
                      className="p-1 rounded hover:bg-white/10 text-slate-300 hover:text-white flex items-center gap-1 transition-all"
                    >
                      {copiedIndex === codeIdx ? (
                        <>
                          <Check className="w-3 h-3 text-emerald-400" />
                          <span className="text-[9px] text-emerald-400">Copied</span>
                        </>
                      ) : (
                        <>
                          <Copy className="w-3 h-3" />
                          <span className="text-[9px]">Copy</span>
                        </>
                      )}
                    </button>
                  </div>
                  {/* Code Body */}
                  <pre className="p-3 font-mono text-xs leading-relaxed text-sky-200 overflow-x-auto selection:bg-fissal-gold/30">
                    <code>{codeString}</code>
                  </pre>
                </div>
              );
            }

            return (
              <code
                className="font-mono text-xs bg-black/50 text-sky-300 px-1.5 py-0.5 rounded border border-white/10 font-semibold"
                {...rest}
              >
                {children}
              </code>
            );
          },

          // Tables
          table({ children }) {
            return (
              <div className="my-2.5 overflow-x-auto rounded-xl border border-white/10">
                <table className="w-full text-left text-xs">{children}</table>
              </div>
            );
          },
          thead({ children }) {
            return <thead className="bg-white/10 text-fissal-gold font-semibold">{children}</thead>;
          },
          th({ children }) {
            return <th className="p-2 border-b border-white/10 font-mono text-[10px] uppercase">{children}</th>;
          },
          td({ children }) {
            return <td className="p-2 border-b border-white/5 text-slate-300">{children}</td>;
          },
        }}
      >
        {content}
      </ReactMarkdown>
    </div>
  );
};
